import { useState } from "react";
import { Plus, Building2 } from "lucide-react";
import {
  superCreateSucursal,
  superUpdateSucursal,
  type SucursalRow,
} from "../../lib/api";
import { Modal } from "../../components/ui/Modal";
import { HelpPopover } from "../../components/admin/forms/HelpPopover";
import { useDialogs } from "../../hooks/useDialogs";

// Cuerpo de la pestaña "Sucursales" dentro de Organizacion. NO trae su
// propio <h1> ni hace fetch: el padre (Organizacion) carga sucursales y
// usuarios en un solo Promise.all y nos pasa la lista + un refetch para
// rehidratar tras crear/editar. Asi evitamos el doble fetch que habia
// cuando Sucursales y Usuarios eran paginas separadas (ambas llamaban
// superListSucursales).
export function SucursalesPanel({
  sucursales,
  refetch,
}: {
  sucursales: SucursalRow[];
  refetch: () => void;
}) {
  const dialogs = useDialogs();
  const [creating, setCreating] = useState(false);

  // Edicion inline de nombre/ciudad (reorg UX 2026-06): editar la celda
  // directo en vez de abrir un modal. Optimista — si el PATCH falla,
  // mostramos el error y refetcheamos para revertir al estado real.
  async function handleUpdate(
    s: SucursalRow,
    patch: { nombre?: string; ciudad?: string | null },
  ) {
    try {
      await superUpdateSucursal(s.id, patch);
      refetch();
    } catch (e) {
      dialogs.alert({
        message: e instanceof Error ? e.message : String(e),
        variant: "error",
      });
      refetch();
    }
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-end gap-2">
        <HelpPopover label="Qué pasa al crear una sucursal" align="right">
          Al crear una sucursal nueva, automáticamente se crean settings
          default y 2 grupos de premios vacíos (uno por juego).
        </HelpPopover>
        <button
          onClick={() => setCreating(true)}
          data-tour="sucursales-new"
          className="flex items-center gap-2 rounded-lg bg-gradient-to-r from-[#a78bfa] to-[#7c3aed] px-4 py-2 font-bold"
        >
          <Plus className="h-4 w-4" />
          Nueva sucursal
        </button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {sucursales.map((s) => (
          <SucursalCard
            key={s.id}
            sucursal={s}
            onUpdate={(patch) => handleUpdate(s, patch)}
          />
        ))}
      </div>

      <Modal open={creating} onClose={() => setCreating(false)} closeOnBackdrop>
        <SucursalForm
          onCancel={() => setCreating(false)}
          onSubmit={async (data) => {
            await superCreateSucursal(data);
            setCreating(false);
            refetch();
          }}
        />
      </Modal>
    </div>
  );
}

// Card de sucursal con nombre + ciudad editables inline. El slug NO se
// edita inline (es identificador estable, cambiarlo rompe referencias);
// para eso conviene el flujo completo.
function SucursalCard({
  sucursal: s,
  onUpdate,
}: {
  sucursal: SucursalRow;
  onUpdate: (patch: { nombre?: string; ciudad?: string | null }) => void;
}) {
  const [editNombre, setEditNombre] = useState(false);
  const [editCiudad, setEditCiudad] = useState(false);

  function commitNombre(value: string) {
    setEditNombre(false);
    const v = value.trim();
    if (v === "" || v === s.nombre) return; // vacio o sin cambio → cancelar
    onUpdate({ nombre: v });
  }
  function commitCiudad(value: string) {
    setEditCiudad(false);
    const v = value.trim();
    const nuevo = v === "" ? null : v;
    if (nuevo === s.ciudad) return;
    onUpdate({ ciudad: nuevo });
  }

  return (
    <div className="rounded-lg bg-white/5 p-4">
      <div className="mb-2 flex items-center gap-2">
        <Building2 className="h-5 w-5 opacity-60" />
        <span className="text-xs uppercase tracking-wider opacity-60">
          {s.slug}
        </span>
        {s.activa === 0 && (
          <span className="rounded bg-red-500/20 px-1.5 py-0.5 text-xs text-red-300">
            Inactiva
          </span>
        )}
      </div>

      {editNombre ? (
        <input
          autoFocus
          defaultValue={s.nombre}
          onBlur={(e) => commitNombre(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commitNombre((e.target as HTMLInputElement).value);
            } else if (e.key === "Escape") {
              setEditNombre(false);
            }
          }}
          className="w-full rounded bg-white/10 px-2 py-1 text-lg font-bold outline-none focus:ring-2 focus:ring-[#a78bfa]"
        />
      ) : (
        <button
          type="button"
          onClick={() => setEditNombre(true)}
          title="Click para editar el nombre"
          className="-mx-1 rounded px-1 text-left text-lg font-bold hover:bg-white/10"
        >
          {s.nombre}
        </button>
      )}

      {editCiudad ? (
        <input
          autoFocus
          defaultValue={s.ciudad ?? ""}
          placeholder="Ciudad (opcional)"
          onBlur={(e) => commitCiudad(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commitCiudad((e.target as HTMLInputElement).value);
            } else if (e.key === "Escape") {
              setEditCiudad(false);
            }
          }}
          className="mt-1 w-full rounded bg-white/10 px-2 py-1 text-sm outline-none focus:ring-2 focus:ring-[#a78bfa]"
        />
      ) : (
        <button
          type="button"
          onClick={() => setEditCiudad(true)}
          title="Click para editar la ciudad"
          className="-mx-1 mt-0.5 block rounded px-1 text-left text-sm opacity-70 hover:bg-white/10 hover:opacity-100"
        >
          {s.ciudad || <span className="italic opacity-50">Sin ciudad</span>}
        </button>
      )}
    </div>
  );
}

function SucursalForm({
  onSubmit,
  onCancel,
}: {
  onSubmit: (data: { nombre: string; slug: string; ciudad?: string }) => Promise<void>;
  onCancel: () => void;
}) {
  const [nombre, setNombre] = useState("");
  const [slug, setSlug] = useState("");
  const [ciudad, setCiudad] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await onSubmit({
        nombre: nombre.trim(),
        slug: slug.trim().toUpperCase(),
        ciudad: ciudad.trim() || undefined,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <h2 className="text-2xl font-bold">Nueva sucursal</h2>

      <label className="flex flex-col gap-1 text-sm">
        <span className="opacity-80">Nombre completo</span>
        <input
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          placeholder="Shopping China Ciudad del Este"
          required
          className="rounded-lg bg-white/10 px-3 py-2 outline-none focus:ring-2 focus:ring-[#a78bfa]"
        />
      </label>

      <label className="flex flex-col gap-1 text-sm">
        <span className="opacity-80">
          Código (corto, mayúsculas, sin espacios — ej. SDG, CDE, PJC)
        </span>
        <input
          value={slug}
          onChange={(e) =>
            setSlug(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ""))
          }
          placeholder="CDE"
          pattern="[A-Z0-9-]+"
          required
          maxLength={20}
          className="rounded-lg bg-white/10 px-3 py-2 outline-none focus:ring-2 focus:ring-[#a78bfa]"
        />
      </label>

      <label className="flex flex-col gap-1 text-sm">
        <span className="opacity-80">Ciudad (opcional)</span>
        <input
          value={ciudad}
          onChange={(e) => setCiudad(e.target.value)}
          placeholder="Ciudad del Este"
          className="rounded-lg bg-white/10 px-3 py-2 outline-none focus:ring-2 focus:ring-[#a78bfa]"
        />
      </label>

      {error && (
        <div className="rounded-lg bg-red-500/15 px-3 py-2 text-sm text-red-300">
          {error}
        </div>
      )}

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 rounded-lg bg-white/10 py-3 font-bold hover:bg-white/20"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={loading}
          className="flex-1 rounded-lg bg-gradient-to-r from-[#a78bfa] to-[#7c3aed] py-3 font-bold disabled:opacity-50"
        >
          {loading ? "Creando…" : "Crear sucursal"}
        </button>
      </div>
    </form>
  );
}
